import {
  ReactNode,
  useContext,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";

import { SysMessageLevel } from "../sys_messages/type";
import { SysMessagesContext } from "../sys_messages/context";
import { useApi, useCustomer } from "../app/provider";
import { VersionedDataContext } from "./context";
import { VersionedDataContextType } from "./type";
import { Run } from "../../services/appsync/types";
import {
  mergeRun,
  runsAppSyncService,
} from "../../services/appsync/service";
import { runsAppsyncEndpoint } from "../../configs/cognito";

// AppSync live-subscription is only viable when the endpoint is configured.
// In local dev the env var is unset, so we fall back to polling-only and
// skip the subscribe call (which would fail and retry forever otherwise).
const APPSYNC_SUBSCRIPTION_ENABLED = !!runsAppsyncEndpoint;

const { Provider } = VersionedDataContext;

export const VersionedDataProvider = (props: {
  children: ReactNode;
}): JSX.Element => {
  const { addSystemMessage } = useContext(SysMessagesContext);
  const { selectedCustomerId } = useCustomer();
  const { apiService } = useApi();

  const [loading, setLoading] = useState<boolean>(true);

  const [runsOrdered, setRunsOrdered] = useState<Run[]>([]);
  const [justTriggeredRun, setJustTriggeredRun] = useState<Run | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isPollingRef = useRef(false);
  const [subscriptionAttempt, setSubscriptionAttempt] = useState(0);

  useEffect(() => {
    let isActive = true;
    let unsubscribe = () => {};

    const scheduleReconnect = () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        setSubscriptionAttempt((attempt) => attempt + 1);
      }, 3000);
    };

    const connectRuns = async () => {
      try {
        const runs = await apiService.getMyRunsSnapshot();
        if (!isActive) {
          return;
        }
        setRunsOrdered(runs);
        setJustTriggeredRun(null);
        setLoading(false);
      } catch (error) {
        if (!isActive) {
          return;
        }
        const message =
          error instanceof Error ? error.message : "Failed to load runs";
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message,
        });
        setLoading(false);
      }

      if (!APPSYNC_SUBSCRIPTION_ENABLED) {
        // Local dev: no AppSync endpoint configured. Skip subscribing;
        // the polling effect below covers in-progress runs.
        return;
      }

      try {
        const subscription = await runsAppSyncService.subscribeToRuns(
          selectedCustomerId,
          {
            onRun: (run) => {
              if (!isActive) {
                return;
              }
              // Only clear justTriggeredRun when the incoming run is actively
              // in progress — not at TO_RUN status, to avoid a timing gap
              // where engineInProgress hasn't flipped true yet.
              if (
                run.status === "RUN_IN_PROGRESS" ||
                run.status === "CANCEL_IN_PROGRESS" ||
                run.status === "RUN_COMPLETED" ||
                run.status === "RUN_FAILED" ||
                run.status === "CANCEL_COMPLETED"
              ) {
                setJustTriggeredRun(null);
              }
              setRunsOrdered((currentRuns) => mergeRun(currentRuns, run));
              setLoading(false);
            },
            onError: (error) => {
              if (!isActive) {
                return;
              }
              setJustTriggeredRun(null);
              addSystemMessage({
                level: SysMessageLevel.WARNING,
                message: error.message,
              });
              scheduleReconnect();
            },
          }
        );
        unsubscribe = subscription.unsubscribe;
      } catch (error) {
        if (!isActive) {
          return;
        }
        const message =
          error instanceof Error
            ? error.message
            : "Failed to subscribe to runs";
        addSystemMessage({
          level: SysMessageLevel.WARNING,
          message,
        });
        scheduleReconnect();
      }
    };

    connectRuns();

    return () => {
      isActive = false;
      unsubscribe();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [addSystemMessage, apiService, selectedCustomerId, subscriptionAttempt]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  const [mainRun, setMainRun] = useState<Run | null>(null);
  const [comparedRun, setComparedRun] = useState<Run | null>(null);
  const [editedRun, setEditedRun] = useState<Run | null>(null);

  useEffect(() => {
    setMainRun((currentRun) =>
      currentRun
        ? runsOrdered.find((run) => run.id === currentRun.id) || currentRun
        : null
    );
    setComparedRun((currentRun) =>
      currentRun
        ? runsOrdered.find((run) => run.id === currentRun.id) || currentRun
        : null
    );
    setEditedRun((currentRun) =>
      currentRun
        ? runsOrdered.find((run) => run.id === currentRun.id) || currentRun
        : null
    );
  }, [runsOrdered]);

  useEffect(() => {
    setMainRun(null);
    setComparedRun(null);
    setEditedRun(null);
    setRunsOrdered([]);
    setJustTriggeredRun(null);
    setLoading(true);
  }, [selectedCustomerId]);

  const lastSuccessfulRun = useMemo(
    () =>
      runsOrdered.find(
        (run) =>
          run.status === "RUN_COMPLETED" &&
          run.runSettings.workflowId === "workflow_atomic_pivots"
      ) || null,
    [runsOrdered]
  );

  const atomicPivotRuns = useMemo(
    () =>
      runsOrdered.filter(
        (run) => run.runSettings.workflowId === "workflow_atomic_pivots"
      ),
    [runsOrdered]
  );

  const googleDriveSyncRuns = useMemo(
    () =>
      runsOrdered.filter(
        (run) => run.runSettings.workflowId === "google_drive_sync"
      ),
    [runsOrdered]
  );

  const runInProgress = useMemo(
    () =>
      runsOrdered.find(
        (run) =>
          run.status === "TO_RUN" ||
          run.status === "RUN_IN_PROGRESS" ||
          run.status === "CANCEL_IN_PROGRESS"
      ) || null,
    [runsOrdered]
  );

  const engineInProgress = useMemo(
    () => runInProgress !== null,
    [runInProgress]
  );

  useEffect(() => {
    let isActive = true;
    const shouldPoll = engineInProgress || justTriggeredRun !== null;

    const pollRunsSnapshot = async () => {
      if (!shouldPoll || isPollingRef.current) {
        return;
      }

      isPollingRef.current = true;
      try {
        const runs = await apiService.getMyRunsSnapshot();
        if (!isActive) {
          return;
        }

        const hasActiveRun = runs.some(
          (run) =>
            run.status === "RUN_IN_PROGRESS" ||
            run.status === "CANCEL_IN_PROGRESS"
        );

        setRunsOrdered(runs);
        setLoading(false);

        // Only clear justTriggeredRun when the run is actively in progress
        // (not just when it appears in the list with TO_RUN status). This
        // prevents a timing gap where justTriggeredRun is cleared but
        // engineInProgress hasn't flipped to true yet.
        if (justTriggeredRun && hasActiveRun) {
          setJustTriggeredRun(null);
        }
      } catch {
        // AppSync remains the fast path; polling only repairs missed live updates.
      } finally {
        isPollingRef.current = false;
        if (isActive && shouldPoll) {
          pollTimeoutRef.current = setTimeout(pollRunsSnapshot, 3000);
        }
      }
    };

    if (shouldPoll) {
      pollTimeoutRef.current = setTimeout(pollRunsSnapshot, 3000);
    }

    return () => {
      isActive = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [apiService, engineInProgress, justTriggeredRun]);

  const value: VersionedDataContextType = {
    loading,
    lastSuccessfulRun,
    atomicPivotRuns,
    googleDriveSyncRuns,
    engineInProgress,
    runInProgress,
    mainRun,
    setMainRun,
    comparedRun,
    setComparedRun,
    runsOrdered,
    justTriggeredRun,
    setJustTriggeredRun,
    editedRun,
    setEditedRun,
  };

  return <Provider value={value}>{props.children}</Provider>;
};
